import { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Input from "../../components/form/input/InputField";
import Label from "../../components/form/Label";
import { Link, useNavigate } from "react-router-dom";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import PageMeta from "../../components/common/PageMeta";
// import { useSettings } from "../../context/SettingsContext";

const videoPaths = [
  "/videos/sales_1.mp4",
  "/videos/sales_2.mp4",
  "/videos/sales_3.mp4",
];

const SignIn = () => {
  const navigate = useNavigate();
  // const { settings } = useSettings();
  const cachedSettings = localStorage.getItem("appSettings");
  const parsedCache = cachedSettings ? JSON.parse(cachedSettings) : null;

  const [logoPath, setLogoPath] = useState<string | null>(
    parsedCache?.logoPath ? `${parsedCache.logoPath}?v=${Date.now()}` : null
  );
  const [logoName, setLogoName] = useState(parsedCache?.logoName || "");




  // Load logo and name from localStorage manually (for non-logged-in users)
  useEffect(() => {
    const cachedSettings = localStorage.getItem("appSettings");
    const parsedCache = cachedSettings ? JSON.parse(cachedSettings) : null;

    // Set favicon and title immediately from cache
    if (parsedCache?.logoPath) {
      const favicon = document.querySelector("link[rel~='icon']");
      if (favicon) {
        favicon.setAttribute("href", `${parsedCache.logoPath}?v=${Date.now()}`);
      }

      document.title = parsedCache.logoName
        ? `${parsedCache.logoName} | Powered by KloudData`
        : "Powered by KloudData";
    }

    const fetchSettings = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/setting/edit`
        );
        if (response.data.status === 200 && response.data.data?.length > 0) {
          const settings = response.data.data[0];
          const logo = settings.logo_path;
          const name = settings.logoname;

          if (logo) {
            setLogoPath(`${logo}?v=${Date.now()}`);
            const favicon = document.querySelector("link[rel~='icon']");
            if (favicon) {
              favicon.setAttribute("href", `${logo}?v=${Date.now()}`);
            }
          }

          if (name?.trim()) {
            setLogoName(name);
            document.title = `${name} | Powered by KloudData`;
          } else {
            setLogoName("");
            document.title = "Powered by KloudData";
          }

          localStorage.setItem(
            "appSettings",
            JSON.stringify({ logoPath: logo, logoName: name })
          );
        }
      } catch (error) {
        console.error("Error fetching logo settings:", error);
      }
    };

    fetchSettings();
  }, []);




  const [currentVideoIndex, setCurrentVideoIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false); // New state for loading

  useEffect(() => {
    const accessToken = sessionStorage.getItem("access_token");

    if (accessToken) {
      navigate("/", { replace: true });
    }
    const randomIndex = Math.floor(Math.random() * videoPaths.length);
    setCurrentVideoIndex(randomIndex);
  }, [navigate]);

  const handleVideoEnd = () => {
    setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videoPaths.length);
  };

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const handleEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setErrors((prev) => ({ ...prev, email: "" }));
  };

  const handlePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    setErrors((prev) => ({ ...prev, password: "" }));
  };

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isLoading) return; // Prevent multiple submissions

    setIsLoading(true); // Set loading to true
    setErrors({ email: "", password: "" });

    const newErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      newErrors.email = "The email field is required.";
    }

    if (!password.trim()) {
      newErrors.password = "The password field is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsLoading(false); // Reset loading state
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/login`,
        {
          email,
          password,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = response.data;

      if (data.status === 422) {
        const apiErrors: { email?: string; password?: string } = {};
        if (data.message?.email) {
          apiErrors.email = data.message.email;
          toast.error(data.message.email, { autoClose: 1500 });
        }
        if (data.message?.password) {
          apiErrors.password = data.message.password;
          toast.error(data.message.password, { autoClose: 1500 });
        }

        setErrors(apiErrors);
        setEmail("");
        setPassword("");
        setIsLoading(false); // Reset loading state
        return;
      }

      if (data.status === 500) {
        toast.error(data.message, { autoClose: 1500 });
        setEmail("");
        setPassword("");
        setIsLoading(false); // Reset loading state
        return;
      }

      if (data.status !== 200 || !data.access_token) {
        toast.error("Invalid Username or Password", { autoClose: 2000 });
        setEmail("");
        setPassword("");
        setIsLoading(false); // Reset loading state
        return;
      }

      if (data.status === 200 && data.access_token) {
        sessionStorage.setItem("access_token", data.access_token);
        sessionStorage.setItem("user_id", data.user_id);
        sessionStorage.setItem("user_roles", JSON.stringify(data.user_roles));

        toast.success("Login successful!", {
          autoClose: 1500,
          style: {
            minWidth: "250px",
            maxWidth: "400px",
          },
        });

        setTimeout(() => {
          navigate("/", { replace: true });
        }, 500);
      }
    } catch (error) {
      console.error("Login Error:", error);
      toast.error("Invalid Username or Password", { autoClose: 2000 });
      setEmail("");
      setPassword("");
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  // const logoSrc = logoPath;
  const displayName = logoName;

  return (
    <>
      <PageMeta
        title="SignIn Page"
        description="This is SignIn Dashboard page"
      />

      <ToastContainer />
      <div className="w-full h-screen flex">
        {/* Left Section - Video */}
        <div className="leftSection relative flex-[3]">
          <video
            className="absolute top-0 left-0 w-full h-full object-cover"
            src={videoPaths[currentVideoIndex]}
            autoPlay
            onEnded={handleVideoEnd}
            muted
          />
          <div className="absolute top-0 left-0 w-full h-20 bg-white opacity-30 mb-4"></div>
          <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
            {logoPath && (
              <img className="h-10" src={logoPath} alt={logoName} />
            )}
            {logoName && (
              <span className="text-4xl font-bold text-gray-800">{logoName}</span>
            )}
          </div>
        </div>

        <div className="rightSection relative flex-[2] flex items-center justify-center">
          <div className="mobileLogoContainer absolute top-9 left-5">
            {logoPath && (
              <img className="h-10" src={logoPath} alt={logoName} />
            )}
            {logoName && (
              <span className="text-4xl font-bold text-gray-700 dark:text-gray-300 mt-2">
                {logoName}
              </span>
            )}
          </div>


          <div className="titleContainer">
            <div>
              <div className="mb-5 sm:mb-8">
                <h1 className="heading">Welcome to {displayName}</h1>
                <p className="subText">
                  Enter your email and password to log in!
                </p>
              </div>

              <form onSubmit={handleLogin}>
                <div className="space-y-6">
                  <div>
                    <Label>
                      Email <span className="text-error-500">*</span>
                    </Label>
                    <Input
                      value={email}
                      onChange={handleEmail}
                      className={`inputField ${errors.email
                        ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                        : "border-gray-300 focus:border-blue-500"
                        }`}
                    />
                    {errors.email && (
                      <span className="errorText">{errors.email}</span>
                    )}
                  </div>

                  <div>
                    <Label>
                      Password <span className="text-error-500">*</span>
                    </Label>
                    <div className="passwordContainer">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={handlePassword}
                        className={`inputField pr-10 ${errors.password
                          ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                          : "border-gray-300 focus:border-blue-500"
                          }`}
                      />
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        className="eyeIcon"
                      >
                        {showPassword ? (
                          <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
                        ) : (
                          <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
                        )}
                      </span>
                    </div>
                    {errors.password && (
                      <span className="errorText">{errors.password}</span>
                    )}
                  </div>

                  <div className="forgotPassword">
                    <Link to="/forgot-password">Forgot password?</Link>
                  </div>

                  <div>
                    <button
                      type="submit"
                      className={`loginButton ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      disabled={isLoading} // Disable button when loading
                    >
                      {isLoading ? "Logging in..." : "Log in"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;